import React, { useState, useEffect } from 'react';
import { Container, Card, Alert, Button, Form, Spinner, Badge } from 'react-bootstrap';
import API_BASE_URL from './config';

function CompanyCities() {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    
    const [companyCities, setCompanyCities] = useState([]);
    const [availableCities, setAvailableCities] = useState({});
    const [selectedCities, setSelectedCities] = useState(new Set());
    const [expandedStates, setExpandedStates] = useState(new Set(['AZ', 'CO', 'NM'])); // Default expand these
    
    const [totalAvailable, setTotalAvailable] = useState(0);
    const [totalSelected, setTotalSelected] = useState(0);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        setError(null);
        
        try {
            // Load available cities and current selection
            const response = await fetch(`${API_BASE_URL}/company/cities/available`, {
                credentials: 'include'
            });
            
            if (!response.ok) {
                throw new Error('Failed to load cities');
            }
            
            const data = await response.json();
            
            setAvailableCities(data.cities_by_state || {});
            setTotalAvailable(data.total_available || 0);
            setTotalSelected(data.total_selected || 0);
            
            // Build set of selected cities
            const selected = new Set();
            Object.values(data.cities_by_state || {}).forEach(cities => {
                cities.forEach(city => {
                    if (city.selected) {
                        selected.add(city.name);
                    }
                });
            });
            setSelectedCities(selected);
            
        } catch (err) {
            console.error('Error loading cities:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const toggleState = (state) => {
        setExpandedStates(prev => {
            const next = new Set(prev);
            if (next.has(state)) {
                next.delete(state);
            } else {
                next.add(state);
            }
            return next;
        });
    };

    const toggleCity = (cityName) => {
        setSelectedCities(prev => {
            const next = new Set(prev);
            if (next.has(cityName)) {
                next.delete(cityName);
            } else {
                next.add(cityName);
            }
            return next;
        });
    };

    const selectAllInState = (state) => {
        const cities = availableCities[state] || [];
        setSelectedCities(prev => {
            const next = new Set(prev);
            cities.forEach(city => {
                next.add(city.name);
            });
            return next;
        });
    };

    const deselectAllInState = (state) => {
        const cities = availableCities[state] || [];
        setSelectedCities(prev => {
            const next = new Set(prev);
            cities.forEach(city => {
                next.delete(city.name);
            });
            return next;
        });
    };

    const saveSelection = async () => {
        setSaving(true);
        setError(null);
        setSuccess(null);
        
        try {
            // Build list of cities to add (with state info)
            const citiesToAdd = [];
            Object.entries(availableCities).forEach(([state, cities]) => {
                cities.forEach(city => {
                    if (selectedCities.has(city.name)) {
                        citiesToAdd.push({
                            city_name: city.name,
                            state: state
                        });
                    }
                });
            });
            
            // First, remove all current cities
            const currentResponse = await fetch(`${API_BASE_URL}/company/cities`, {
                credentials: 'include'
            });
            const currentData = await currentResponse.json();
            
            // Delete cities that are no longer selected
            const deletePromises = currentData.cities
                .filter(city => !selectedCities.has(city.city_name))
                .map(city => 
                    fetch(`${API_BASE_URL}/company/cities/${city.id}`, {
                        method: 'DELETE',
                        credentials: 'include'
                    })
                );
            
            await Promise.all(deletePromises);
            
            // Add new cities
            const response = await fetch(`${API_BASE_URL}/company/cities`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify({
                    cities: citiesToAdd
                })
            });
            
            if (!response.ok) {
                throw new Error('Failed to save cities');
            }
            
            const result = await response.json();
            setSuccess(`Saved! ${result.added} cities added, ${deletePromises.length} removed.`);
            
            // Reload data
            setTimeout(() => {
                loadData();
            }, 1500);
            
        } catch (err) {
            console.error('Error saving cities:', err);
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Container className="my-4">
                <div className="text-center">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-2">Loading cities...</p>
                </div>
            </Container>
        );
    }

    const stateNames = {
        'AZ': 'Arizona',
        'CO': 'Colorado',
        'NM': 'New Mexico',
        'FL': 'Florida',
        'GA': 'Georgia',
        'TX': 'Texas',
        'OTHER': 'Other'
    };

    const sortedStates = Object.keys(availableCities).sort();

    return (
        <Container className="my-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h2>📍 Manage Tracked Cities</h2>
                    <p className="text-muted">
                        Select which cities your company wants to track. Only jobs from selected cities will appear in your RFQ list.
                    </p>
                </div>
                <div className="text-end">
                    <Badge bg="secondary" className="me-2">
                        {selectedCities.size} of {totalAvailable} selected
                    </Badge>
                    <Button 
                        variant="success" 
                        onClick={saveSelection}
                        disabled={saving}
                    >
                        {saving ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Saving...
                            </>
                        ) : (
                            '💾 Save Selection'
                        )}
                    </Button>
                </div>
            </div>

            {error && (
                <Alert variant="danger" dismissible onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {success && (
                <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
                    {success}
                </Alert>
            )}

            {sortedStates.map(state => {
                const cities = availableCities[state] || [];
                const stateCityCount = cities.length;
                const selectedInState = cities.filter(c => selectedCities.has(c.name)).length;
                const isExpanded = expandedStates.has(state);
                
                return (
                    <Card key={state} className="mb-3">
                        <Card.Header 
                            style={{ cursor: 'pointer', userSelect: 'none' }}
                            onClick={() => toggleState(state)}
                        >
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <strong>{stateNames[state] || state}</strong>
                                    <Badge bg="secondary" className="ms-2">
                                        {selectedInState} / {stateCityCount} selected
                                    </Badge>
                                </div>
                                <div>
                                    {isExpanded && (
                                        <>
                                            <Button 
                                                size="sm" 
                                                variant="outline-primary" 
                                                className="me-2"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    selectAllInState(state);
                                                }}
                                            >
                                                Select All
                                            </Button>
                                            <Button 
                                                size="sm" 
                                                variant="outline-secondary" 
                                                className="me-2"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    deselectAllInState(state);
                                                }}
                                            >
                                                Deselect All
                                            </Button>
                                        </>
                                    )}
                                    <span className="me-2">{isExpanded ? '▼' : '▶'}</span>
                                </div>
                            </div>
                        </Card.Header>
                        
                        {isExpanded && (
                            <Card.Body>
                                <div className="row">
                                    {cities.map(city => (
                                        <div key={city.name} className="col-md-6 col-lg-4 mb-2">
                                            <Form.Check
                                                type="checkbox"
                                                id={`city-${city.name}`}
                                                label={city.name}
                                                checked={selectedCities.has(city.name)}
                                                onChange={() => toggleCity(city.name)}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </Card.Body>
                        )}
                    </Card>
                );
            })}

            <div className="mt-4 text-center">
                <Button 
                    variant="success" 
                    size="lg"
                    onClick={saveSelection}
                    disabled={saving}
                >
                    {saving ? (
                        <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Saving...
                        </>
                    ) : (
                        '💾 Save Selection'
                    )}
                </Button>
            </div>

            <div className="mt-4 p-3 bg-light rounded">
                <h5>ℹ️ How This Works</h5>
                <ul>
                    <li><strong>Company-Level:</strong> All users in your company see jobs from the selected cities</li>
                    <li><strong>Filtered View:</strong> Only RFQs from checked cities will appear in your job list</li>
                    <li><strong>Collaborative:</strong> Changes affect all users in your company immediately after saving</li>
                    <li><strong>Flexible:</strong> You can add/remove cities anytime to adjust your tracking scope</li>
                </ul>
            </div>
        </Container>
    );
}

export default CompanyCities;

